import { useEffect, useState } from 'react';
import { Box, Chip, MenuItem, Paper, TextField, Typography } from '@mui/material';
import { NumericTextField } from '../../../../components/forms/NumericTextField';
import type { RockUCSNSGeneral, RockUCSNSSpecimen } from '../../../../types/rockUcsNs';
import type { UcsNsSpecimenPreview } from '../computations';
import { fetchMachines, type MachineRow } from '../../../../services/scheduleMachines';
import { DerivedValue, fmt, inputSx, paperSx, SectionHeader, unit } from '../../rock-ucs/sectionStyles';
import { useTestFormulas } from '../../../../hooks/useTestFormulas';

interface Props {
  general: RockUCSNSGeneral;
  specimen: RockUCSNSSpecimen;
  preview: UcsNsSpecimenPreview;
  onGeneralChange: (field: keyof RockUCSNSGeneral, value: RockUCSNSGeneral[keyof RockUCSNSGeneral]) => void;
  onSpecimenChange: (field: keyof RockUCSNSSpecimen, value: RockUCSNSSpecimen[keyof RockUCSNSSpecimen]) => void;
  readOnly?: boolean;
}

/**
 * BUG-2026-0138 item 12 — "4. Compression Test Setup", the UCS-NS analogue of
 * rock-ucs/sections/CompressionSetupSection: machine, test date, tested-by,
 * plus Test B's Rate of Loading (kN/s) with the derived stress rate and the
 * ASTM D7012 Method C guidance band (0.5–1.0 MPa/s × area). No gauge lengths,
 * chain length, or rate of axial strain — Test B has no strain gauges.
 */
export const UcsNsTestSetupSection = ({
  general, specimen, preview, onGeneralChange, onSpecimenChange, readOnly,
}: Props) => {
  const [machines, setMachines] = useState<MachineRow[]>([]);
  // Formula-registry tooltips for the computed tiles (FEAT "Formula Registry").
  const { formulaFor } = useTestFormulas('rockUcsNs');

  useEffect(() => {
    let cancelled = false;
    fetchMachines()
      .then((rows) => {
        if (cancelled) return;
        // Rock compression machines first, then the rest of the rock fleet
        const sorted = [...rows].sort((a, b) => {
          const aRk = a.Code?.startsWith('RK-') ? 0 : 1;
          const bRk = b.Code?.startsWith('RK-') ? 0 : 1;
          return aRk - bRk || (a.Code ?? '').localeCompare(b.Code ?? '');
        });
        setMachines(sorted);
      })
      .catch(() => setMachines([]));
    return () => { cancelled = true; };
  }, []);

  return (
    <Paper variant="outlined" sx={paperSx}>
      <SectionHeader label="4. Compression Test Setup" />
      <Box sx={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr', gap: 1.5 }}>
        {/* Machine / date / tested-by */}
        <Box sx={{ display: 'grid', rowGap: 1.25, alignContent: 'start' }}>
          <TextField
            select size="small" label="Machine"
            value={general.machineKey ?? ''}
            onChange={(e) => onGeneralChange('machineKey', e.target.value || null)}
            InputProps={{ readOnly }} sx={inputSx}
          >
            <MenuItem value="">—</MenuItem>
            {machines.map((m) => (
              <MenuItem key={m.MachineKey} value={m.MachineKey}>
                {m.Code} — {m.Name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small" label="Test Date" type="date"
            value={general.testDate ? String(general.testDate).slice(0, 10) : ''}
            onChange={(e) => onGeneralChange('testDate', e.target.value || null)}
            InputLabelProps={{ shrink: true }}
            InputProps={{ readOnly }} sx={inputSx}
          />
          <TextField
            size="small" label="Tested By (initials)"
            value={general.testedBy ?? ''}
            onChange={(e) => onGeneralChange('testedBy', e.target.value || null)}
            InputProps={{ readOnly }} sx={inputSx}
          />
        </Box>

        {/* Rate of loading (Test B input — workbook K38) */}
        <Box sx={{ display: 'grid', rowGap: 1.25, alignContent: 'start' }}>
          <NumericTextField
            size="small" label="Rate of Loading"
            value={specimen.rateOfLoadingKNs}
            onChange={(v) => onSpecimenChange('rateOfLoadingKNs', v)}
            InputProps={{ endAdornment: unit('kN/s'), readOnly }} sx={inputSx}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <DerivedValue
              label="Stress Rate"
              value={fmt(preview.stressRateMPaS, 3)}
              suffix="MPa/s"
              formula={formulaFor('ComputedStressRateMPaS')?.formula}
              note={formulaFor('ComputedStressRateMPaS')?.note}
            />
            {preview.rateOutsideAstmRange !== null && (
              <Chip
                size="small"
                label={preview.rateOutsideAstmRange ? 'Outside ASTM' : 'In range'}
                color={preview.rateOutsideAstmRange ? 'warning' : 'success'}
                sx={{ height: 18, fontSize: '0.64rem' }}
              />
            )}
          </Box>
        </Box>

        {/* ASTM guidance band (derived from area) */}
        <Box sx={{ display: 'grid', rowGap: 0.5, alignContent: 'start', px: 1 }}>
          <SectionHeader label="Derived" color="text.secondary" />
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
            ASTM D7012 Method C band:
          </Typography>
          <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
            {preview.minRateKNs !== null && preview.maxRateKNs !== null
              ? `${fmt(preview.minRateKNs, 3)} – ${fmt(preview.maxRateKNs, 3)} kN/s`
              : '—'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
            (0.5–1.0 MPa/s × area)
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};
